import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { getClients, deleteClient } from '../services/clientService';
import { resolveAssetUrl } from '../utils/assetUrl';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function ClientsPage() {
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedClients,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(clients, 10);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const data = await getClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await deleteClient(id);
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Operations"
          title="Clients"
          description="Review case study clients, brand assets, and supporting public-facing content."
          meta={<Chip label={`${clients.length} clients`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/clients/create')}>
              Add Client
            </Button>
          }
        />

        {isMobile ? (
          clients.length ? (
            <Grid container spacing={2}>
              {paginatedClients.map((client) => (
                <Grid item xs={12} key={client.clientId}>
                  <AdminSection>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={client.logoUrl ? resolveAssetUrl(client.logoUrl) : undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, borderRadius: 2 }}
                        >
                          {client.name?.charAt(0)}
                        </Avatar>
                        <Stack sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="h6">{client.name}</Typography>
                          <Typography color="text.secondary">{client.title || '-'}</Typography>
                        </Stack>
                      </Stack>
                      <Typography color="text.secondary">
                        {String(client.description || '').replace(/<[^>]*>/g, '').slice(0, 160) || 'No description'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => navigate(`/clients/edit/${client.clientId}`)}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(client.clientId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No clients yet"
                description="Add client entries to power the public client stories and logos."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Logo</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length ? (
                  paginatedClients.map((client) => (
                    <TableRow key={client.clientId}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600 }}>{client.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {client.slug || '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{client.title || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography noWrap color="text.secondary">
                          {String(client.description || '').replace(/<[^>]*>/g, '') || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={client.logoUrl ? resolveAssetUrl(client.logoUrl) : undefined}
                          variant="rounded"
                          sx={{ width: 44, height: 44, borderRadius: 2 }}
                        >
                          {client.name?.charAt(0)}
                        </Avatar>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/clients/edit/${client.clientId}`)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(client.clientId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <AdminEmptyState
                        title="No clients yet"
                        description="Add client entries to power the public client stories and logos."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default ClientsPage;
